/**
 * MobileSettings Component for LOOPINO AI Platform
 * Mobile-optimized settings screen - Instagram connection only
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import './MobileSettings.css';
import { useAuthContext } from '../../../context/AuthContext';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { Trans } from 'react-i18next';
import { Skeleton } from '../../effects';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';

// Importo le icone dei social
import iconInstagram from '../../../assets/images/icons/icon-instagram.svg';
// Importo le icone per il DNA aziendale
import iconStrategy from '../../../assets/images/icons/icon-ai-dna.svg';
// Importo l'icona logout
import iconLogout from '../../../assets/images/icons/icon-logout.svg';


const MobileSettings = ({ onBack, onProfileClick, onLogout }) => {
  // Authentication context
  const { logout: authLogout, isLoading: authLoading, user } = useAuthContext();
  const {
    t
  } = useLanguage();
  const { notify, confirm } = useMobileAlert();

  // API hook for making requests
  const { makeRequest } = useApi();

  // Stati per gestire la connessione Instagram
  const [isInstagramConnected, setIsInstagramConnected] = useState(false);

  // Stato per il profilo Instagram connesso
  const [instagramProfile, setInstagramProfile] = useState(null);

  // Stato per gestire il loading
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlatform, setLoadingPlatform] = useState(null);
  const [error, setError] = useState(null);

  // PWA Installation states
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const iosSteps = useMemo(() => ([
    { number: '1', mainKey: 'settings.app.ios.steps.share.main', hintKey: 'settings.app.ios.steps.share.hint' },
    { number: '2', mainKey: 'settings.app.ios.steps.addToHome.main', hintKey: 'settings.app.ios.steps.addToHome.hint' },
    { number: '3', mainKey: 'settings.app.ios.steps.confirm.main', hintKey: 'settings.app.ios.steps.confirm.hint' }
  ]), []);

  const legalLinks = useMemo(() => ([
    {
      key: 'privacy',
      href: '/privacy',
      label: t('settings.legal.privacy'),
      description: t('settings.legal.privacyDescription')
    },
    {
      key: 'terms',
      href: '/terms',
      label: t('settings.legal.terms'),
      description: t('settings.legal.termsDescription')
    },
    {
      key: 'cookies',
      href: '/cookies',
      label: t('settings.legal.cookies'),
      description: t('settings.legal.cookiesDescription')
    },
    {
      key: 'notice',
      href: '/legal',
      label: t('settings.legal.notice'),
      description: t('settings.legal.noticeDescription')
    }
  ]), [t]);

  const renderLoadingSkeleton = () => (
    <div className="mobile-settings">
      <div className="mobile-settings-content">
        <div className="mobile-settings-section">
          <Skeleton width="50%" height={20} shimmer style={{ marginBottom: 16 }} />
          <div className="mobile-social-connection-item">
            <div className="mobile-social-info">
              <Skeleton variant="circle" width={44} height={44} shimmer />
              <div className="mobile-social-details">
                <Skeleton width="80%" height={14} shimmer />
                <Skeleton width="60%" height={12} shimmer style={{ marginTop: 6 }} />
              </div>
            </div>
            <Skeleton width={120} height={36} shimmer />
          </div>
        </div>

        <div className="mobile-settings-section">
          <Skeleton width="60%" height={20} shimmer style={{ marginBottom: 16 }} />
          <div className="mobile-profile-section">
            <div className="mobile-profile-item">
              <div className="mobile-profile-info">
                <Skeleton variant="circle" width={48} height={48} shimmer />
                <div className="mobile-profile-details">
                  <Skeleton width="70%" height={16} shimmer />
                  <Skeleton width="90%" height={12} shimmer style={{ marginTop: 6 }} />
                </div>
              </div>
              <Skeleton width={24} height={24} shimmer />
            </div>
          </div>
        </div>

        <div className="mobile-settings-section">
          <Skeleton width="60%" height={20} shimmer style={{ marginBottom: 16 }} />
          <div className="mobile-pwa-section">
            <div className="mobile-pwa-item">
              <Skeleton variant="circle" width={48} height={48} shimmer />
              <div className="mobile-pwa-info">
                <Skeleton width="70%" height={16} shimmer />
                <Skeleton width="80%" height={12} shimmer style={{ marginTop: 6 }} />
              </div>
              <Skeleton width={110} height={32} shimmer />
            </div>
          </div>
        </div>

        <div className="mobile-settings-section">
          <Skeleton width="55%" height={20} shimmer style={{ marginBottom: 16 }} />
          <div className="mobile-general-settings">
            {[1, 2].map((item) => (
              <div key={`setting-skeleton-${item}`} className="mobile-setting-item">
                <div className="mobile-setting-info">
                  <Skeleton width="50%" height={14} shimmer />
                  <Skeleton width="80%" height={12} shimmer style={{ marginTop: 6 }} />
                </div>
                <Skeleton width={60} height={26} shimmer />
              </div>
            ))}
          </div>
        </div>

        <div className="mobile-settings-section mobile-logout-section">
          <div className="mobile-logout-item">
            <Skeleton variant="circle" width={40} height={40} shimmer />
            <div className="mobile-logout-details" style={{ flex: 1 }}>
              <Skeleton width="50%" height={14} shimmer />
              <Skeleton width="70%" height={12} shimmer style={{ marginTop: 6 }} />
            </div>
            <Skeleton width={24} height={24} shimmer />
          </div>
        </div>
      </div>
    </div>
  );

  const fetchConnectedAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await makeRequest('/api/v1/social/status', {
        method: 'GET'
      });
      const instagramData = data.platforms?.instagram || { connected: false };

      setIsInstagramConnected(instagramData.connected);
      setInstagramProfile(instagramData.connected ? { name: instagramData.username } : null);
    } catch (err) {
      setError(t('settings.errors.loadInstagram', { error: err.message }));
      console.error('Errore nel caricamento dell\'account Instagram:', err);
    } finally {
      setIsLoading(false);
    }
  }, [authLogout, t]);

  // Carica lo stato iniziale delle connessioni all'avvio
  useEffect(() => {
    fetchConnectedAccounts();
  }, [fetchConnectedAccounts]);

  // Handle OAuth callback (when user returns from Instagram)
  useEffect(() => {
    const handleOAuthCallback = () => {
      // Check if we're coming back from an OAuth flow via sessionStorage
      const callbackData = sessionStorage.getItem('oauth_callback_data');
      if (callbackData) {
        try {
          const parsedData = JSON.parse(callbackData);
          console.log('[MobileSettings] OAuth callback detected from sessionStorage:', parsedData);

          // Clear the callback data
          sessionStorage.removeItem('oauth_callback_data');

          // Refresh the connections
          fetchConnectedAccounts();

          // Show success/error message
          if (parsedData.success) {
            setError(null);
          } else {
            setError(t('settings.errors.oauthConnect', {
              platform: parsedData.platform,
              error: parsedData.error
            }));
          }
        } catch (err) {
          console.error('[MobileSettings] Error parsing callback data:', err);
          setError(t('settings.errors.oauthCallback'));
        }
      }

      // Also check URL params for direct callback (fallback)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code && state) {
        // OAuth callback detected - refresh the connections
        console.log('[MobileSettings] OAuth callback detected from URL params, refreshing connections...');
        fetchConnectedAccounts();

        // Clean up the URL
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    };

    handleOAuthCallback();
  }, [fetchConnectedAccounts, t]);

  // PWA Installation setup
  useEffect(() => {
    // Check if already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    setIsPWAInstalled(isInstalled);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Funzione per disconnettere Instagram
  const handleInstagramDisconnect = useCallback(async () => {
    try {
      setError(null);
      setLoadingPlatform('instagram');

      await makeRequest('/api/v1/social/disconnect/instagram', {
        method: 'DELETE',
        data: { confirm: true }
      });

      // Aggiorna lo stato
      setIsInstagramConnected(false);
      setInstagramProfile(null);

    } catch (err) {
      setError(t('settings.errors.disconnectInstagram', { error: err.message }));
      console.error('Errore di disconnessione Instagram:', err);
    } finally {
      setLoadingPlatform(null);
    }
  }, [authLogout, t]);

  // Funzione per gestire la connessione Instagram
  const handleInstagramConnect = useCallback(async () => {
    // Se il profilo è già connesso, disconnettilo
    if (isInstagramConnected) {
      await handleInstagramDisconnect();
      return;
    }

    try {
      setError(null);
      setLoadingPlatform('instagram');

      console.log('[MobileSettings] Avvio processo di connessione per Instagram');

      // Chiama l'API per ottenere l'URL OAuth
      const response = await makeRequest('/api/v1/social/connect/instagram', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        data: {}
      });

      if (response.oauth_url) {
        console.log('[MobileSettings] Reindirizzamento a Instagram OAuth:', response.oauth_url);
        // Reindirizza a Instagram OAuth (stesso comportamento del desktop)
        window.location.href = response.oauth_url;
        return; // Non continuare - l'utente verrà reindirizzato
      } else {
        throw new Error('No OAuth URL received from server');
      }

    } catch (err) {
      console.error('[MobileSettings] Errore di autenticazione Instagram:', err);
      setError(t('settings.errors.connectInstagram', { error: err.message }));
    } finally {
      setLoadingPlatform(null);
    }
  }, [isInstagramConnected, handleInstagramDisconnect, makeRequest, t]);



  // Funzione per gestire il logout
  const handleLogout = useCallback(async () => {
    try {
      console.log('[MobileSettings] User logout requested');
      
      // Conferma dall'utente prima di procedere
      const confirmed = await confirm({
        title: t('settings.logout.confirm'),
        variant: 'warning',
        confirmText: t('settings.logout.confirmButton', { defaultValue: 'Logout' }),
        cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
      });

      if (confirmed) {
        console.log('[MobileSettings] Logout confermato, procedendo...');
        
        // Chiama la funzione di logout dal context di autenticazione
        await authLogout();
        
        console.log('[MobileSettings] Logout completato, navigando alla pagina login...');
        
        // Naviga alla pagina di login se la funzione è disponibile
        if (onLogout) {
          onLogout();
        } else {
          console.warn('[MobileSettings] onLogout prop non disponibile, logout completato ma senza navigazione');
        }
      } else {
        console.log('[MobileSettings] Logout annullato dall\'utente');
      }
    } catch (err) {
      console.error('[MobileSettings] Errore durante il logout:', err);
      setError(t('settings.errors.logout', { error: err.message }));
    }
  }, [authLogout, confirm, onLogout, t]);

  // Handler per navigare al DNA aziendale
  const handleDNAClick = useCallback(() => {
    console.log('[MobileSettings] Navigating to Company Profile');
    if (onProfileClick) {
      onProfileClick();
    }
  }, [onProfileClick]);

  // Handler per l'installazione PWA
  const handlePWAInstall = useCallback(async () => {
    if (isPWAInstalled) {
      notify({
        variant: 'info',
        title: t('settings.app.alerts.alreadyInstalled')
      });
      return;
    }

    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) {
      notify({
        variant: 'warning',
        title: t('settings.app.alerts.unavailable')
      });
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('[MobileSettings] PWA installed successfully');
        setIsPWAInstalled(true);
      }

      setDeferredPrompt(null);
    } catch (err) {
      console.error('[MobileSettings] PWA installation error:', err);
      setError(t('settings.errors.pwaInstall', { error: err.message }));
    }
  }, [deferredPrompt, isIOS, isPWAInstalled, notify, t]);

  // Rendering del componente connessione Instagram
  const renderInstagramConnection = () => {
    const isLoading = loadingPlatform === 'instagram';
    const buttonLabel = isLoading
      ? t('common.loading')
      : isInstagramConnected
        ? t('settings.instagram.disconnect')
        : t('settings.instagram.connect');
    const profileLabel = isInstagramConnected && instagramProfile
      ? t('settings.instagram.connectedAs', { name: instagramProfile.name })
      : null;

    return (
      <div className="mobile-social-connection-item">
        <div className="mobile-social-info">
          <img src={iconInstagram} alt="Instagram" className="mobile-social-icon" />
          <div className="mobile-social-details">
            <span className="mobile-social-name">Instagram</span>
            {profileLabel && (
              <span className="mobile-profile-name">{profileLabel}</span>
            )}
          </div>
        </div>
        <button 
          className={`mobile-connect-button ${isInstagramConnected ? 'connected' : ''}`}
          onClick={handleInstagramConnect}
          disabled={isLoading}
        >
          {buttonLabel}
        </button>
      </div>
    );
  };

  if (isLoading) {
    return renderLoadingSkeleton();
  }

  return (
    <div className="mobile-settings">
      <div className="mobile-settings-content">
        {error && (
          <div className="mobile-settings-error">
            {error}
          </div>
        )}

        {/* Sezione Connessione Instagram */}
        <div className="mobile-settings-section">
          <h2 className="mobile-section-title">{t('settings.instagram.title')}</h2>
          <div className="mobile-social-connections">
            {renderInstagramConnection()}
          </div>
        </div>

        {/* Sezione DNA Aziendale */}
        <div className="mobile-settings-section">
          <h2 className="mobile-section-title">{t('settings.companyProfile.title')}</h2>
          <div className="mobile-profile-section" onClick={handleDNAClick}>
            <div className="mobile-profile-item">
              <div className="mobile-profile-info">
                <img src={iconStrategy} alt="DNA Aziendale" className="mobile-profile-icon" />
                <div className="mobile-profile-details">
                  <span className="mobile-profile-name">{t('settings.companyProfile.cardTitle')}</span>
                  <span className="mobile-profile-description">
                    {t('settings.companyProfile.cardDescription')}
                  </span>
                </div>
              </div>
              <div className="mobile-profile-arrow">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        

        {/* Sezione Installazione PWA */}
        <div className="mobile-settings-section">
          <h2 className="mobile-section-title">{t('settings.app.title')}</h2>
          <div className="mobile-pwa-section" onClick={handlePWAInstall}>
            <div className="mobile-pwa-item">
              <div className="mobile-pwa-icon-container">
                <img src="/icon-512.png.svg" alt="LOOPINO AI" className="mobile-pwa-icon" />
              </div>
              <div className="mobile-pwa-info">
                <div className="mobile-pwa-details">
                  <span className="mobile-pwa-name">{t('settings.app.installTitle')}</span>
                  <span className="mobile-pwa-description">
                    {isPWAInstalled
                      ? t('settings.app.installedDescription')
                      : t('settings.app.installDescription')}
                  </span>
                </div>
                {!isPWAInstalled && (
                  <button className="mobile-pwa-install-btn">
                    {isIOS ? t('settings.app.actions.guide') : t('settings.app.actions.install')}
                  </button>
                )}
                {isPWAInstalled && (
                  <div className="mobile-pwa-installed-badge">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* iOS Installation Guide Modal */}
        {showIOSGuide && (
          <div className="mobile-pwa-ios-overlay" onClick={() => setShowIOSGuide(false)}>
            <div className="mobile-pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
              <button className="mobile-pwa-ios-close" onClick={() => setShowIOSGuide(false)}>
                ✕
              </button>

              <h2>{t('settings.app.ios.title')}</h2>

              <div className="mobile-pwa-ios-steps">
                {iosSteps.map((step) => (
                  <div className="mobile-pwa-ios-step" key={step.number}>
                    <div className="mobile-pwa-ios-step-number">{step.number}</div>
                    <div className="mobile-pwa-ios-step-content">
                      <p>
                        <Trans i18nKey={step.mainKey} components={{ strong: <strong /> }} />
                      </p>
                      <p className="mobile-pwa-ios-step-hint">{t(step.hintKey)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button className="mobile-pwa-ios-btn" onClick={() => setShowIOSGuide(false)}>
                {t('settings.app.ios.close')}
              </button>
            </div>
          </div>
        )}

        {/* Sezione Legale */}
        <div className="mobile-settings-section">
          <h2 className="mobile-section-title">{t('settings.legal.title')}</h2>
          <div className="mobile-general-settings mobile-legal-links">
            {legalLinks.map((link) => (
              <a key={link.key} href={link.href} className="mobile-setting-item mobile-legal-link">
                <div className="mobile-setting-info">
                  <span className="mobile-setting-name">{link.label}</span>
                  <p className="mobile-setting-description">{link.description}</p>
                </div>
                <div className="mobile-legal-arrow">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Sezione Logout */}
        <div className="mobile-settings-section mobile-logout-section">
          <div className="mobile-logout-item" onClick={handleLogout}>
            <div className="mobile-logout-info">
              <img src={iconLogout} alt="Logout" className="mobile-logout-icon" />
              <div className="mobile-logout-details">
                <span className="mobile-logout-name">{t('settings.logout.title')}</span>
                <span className="mobile-logout-description">
                  {t('settings.logout.description')}
                </span>
              </div>
            </div>
            <div className="mobile-logout-arrow">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSettings;
